import { cloneDeep as _cloneDeep, get, includes } from 'lodash';

import { CAMPAIGN_STATUSES } from '../../../../constants/EmployerCampaignStatuses';
import { CAMPAIGN_CONTACT_CHANNEL_TYPES } from '../../../../constants/ContactChannelTypes';
import {
    EMPLOYER_ROLE,
    TALENT_EXPERT_ROLE,
} from '../../../../constants/UserRole';

export const CAMPAIGN_TYPES = {
    EMPLOYER: 'employer',
    POSITION: 'position',
    SYSTEM: 'system',
};

export const MAX_MESSAGES_COUNT_LINKEDIN = 2;
export const MAX_MESSAGES_COUNT_EMAIL = 3;

export const cloneCampaignAndUpdateStatus = (campaign, newStatus) => {
    const campaignClone = _cloneDeep(campaign);
    const { status } = campaignClone;

    const now = new Date();
    const newItem = {
        status: status.current,
        timestamp: status.timestamp,
    };

    status.current = newStatus;
    status.timestamp = now;
    status.history.unshift(newItem);

    return campaignClone;
};

export const getSwitchedEmployerCampaignStatus = (campaign, originalCampaign, isCreateMode) => {
    const now = new Date();
    const originalCampaignClone = _cloneDeep(originalCampaign);
    const originalCampaignStatus = get(originalCampaignClone, 'status', {});
    const originalCampaignStatusName = get(originalCampaignStatus, 'current', CAMPAIGN_STATUSES.PAUSED);

    const campaignClone = _cloneDeep(campaign);
    const campaignStatus = get(campaignClone, 'status', {});
    const campaignStatusName = get(campaignStatus, 'current', CAMPAIGN_STATUSES.PAUSED);

    const campaignNewStatusName = campaignStatusName === CAMPAIGN_STATUSES.ACTIVE ? CAMPAIGN_STATUSES.PAUSED : CAMPAIGN_STATUSES.ACTIVE;

    if (isCreateMode) {
        return {
            current: campaignNewStatusName,
            timestamp: now,
            history: [],
        };
    }

    const shouldAddToHistory = (campaignNewStatusName !== originalCampaignStatusName);

    if (shouldAddToHistory) {
        const newHistoryItem = {
            status: originalCampaignStatus.current,
            timestamp: originalCampaignStatus.timestamp,
        };

        campaignStatus.current = campaignNewStatusName;
        campaignStatus.timestamp = now;
        campaignStatus.history = [newHistoryItem, ...campaignStatus.history];
        return {
            current: campaignNewStatusName,
            timestamp: now,
            history: [newHistoryItem, ...campaignStatus.history],
        };
    }
    return originalCampaignStatus;
};

export const getIsAllCampaignContactChannelsDisabled = (campaign, availableContactChannels = []) => availableContactChannels.every(
    (channel) => includes(campaign?.disabledChannels, channel),
);

export const getCampaignDisabledTooltip = ({
    isSystemCampaign,
    isSyncedCampaign,
    isCampaignCreatedByAnotherEmployer, // can be created by reseller and visible for associated employer
    isDefaultCampaignForAssociatedEmployer,
}) => {
    const systemCampaign = {
        label: 'READ_ONLY_SYSTEM_CAMPAIGN_TOOLTIP_LABEL',
        defaultLabel: 'System Campaign Template read only',
    };

    const syncedCampaign = {
        label: 'READ_ONLY_SYNCED_CAMPAIGN_TOOLTIP_LABEL',
        defaultLabel: 'Campaign setting is synced with "System Default Campaign Click “…” and “Edit Campaign” to Edit & save current Job Campaign',
    };

    // if Associated Employer open Reseller campaign
    const resellerCampaign = {
        label: 'READ_ONLY_RESELLER_CAMPAIGN_TOOLTIP_LABEL',
        defaultLabel: 'Reseller Campaign Template read only',
    };

    // if Reseller open campaign, and campaign selected as default (in advanced settings) for Associated Employer
    const resellerCampaignDefaultForAssociatedEmployer = {
        label: 'RESELLER_CAMPAIGN_SELECTED_AS_DEFAULT_FOR_ASSOCIATED_EMPLOYER_TOOLTIP_LABEL',
        defaultLabel: 'Reseller Default Campaign - Read Only',
    };

    switch (true) {
        case isSystemCampaign: return systemCampaign;
        case isSyncedCampaign: return syncedCampaign;
        case isCampaignCreatedByAnotherEmployer: return resellerCampaign;
        case (isDefaultCampaignForAssociatedEmployer): return resellerCampaignDefaultForAssociatedEmployer;

        default: return { label: '', defaultLabel: '' };
    }
};

/*
* For Reseller view only, and Campaigns with type='employer'
* Is campaign selected into Reseller Advance settings as Default for Associated Employer.
* */
export const getIsDefaultCampaignForAssociatedEmployer = (campaignId, employer) => {
    const isReseller = employer?.settings?.isResellerPartner;
    const defaultForEmployerRepCampaignId = get(employer, 'defaultAssociatedEmployersSettings.defaultForEmployerRepCampaignId', null);
    const defaultForRecruiterCampaignId = get(employer, 'defaultAssociatedEmployersSettings.defaultForRecruiterCampaignId', null);

    return isReseller && [defaultForRecruiterCampaignId, defaultForEmployerRepCampaignId].includes(campaignId);
};

export const isSystemTypeCampaign = (campaign) => campaign?.type === CAMPAIGN_TYPES.SYSTEM;

export const isEmployerTypeCampaign = (campaign) => campaign?.type === CAMPAIGN_TYPES.EMPLOYER;

export const isJobTypeCampaign = (campaign) => ![CAMPAIGN_TYPES.EMPLOYER, CAMPAIGN_TYPES.SYSTEM].includes(campaign?.type) && !!campaign?.positionId;

export const isSystemDefaultTECampaign = (campaign) => isSystemTypeCampaign(campaign) && campaign?.role === TALENT_EXPERT_ROLE;

export const isSystemDefaultEMCampaign = (campaign) => isSystemTypeCampaign(campaign) && campaign?.role === EMPLOYER_ROLE;

export const isCampaignSynced = (campaign) => !!campaign?.syncedCampaignId;

export const isCreatedByAnotherEmployer = (campaign, employerId) => !!campaign?.employerId && employerId !== campaign.employerId;
